import type { ServiceSlug, CitySlug } from "./site";
import { IMAGES } from "./images";

export type ChecklistItem = { label: string; detail?: string };

export type ServiceContent = {
  slug: ServiceSlug;
  name: string;
  h1: string;
  intro: string; // answer-first 1-2 sentences
  metaTitle: string;
  metaDescription: string;
  heroImage: string;
  heroAlt: string;
  whatsIncluded: ChecklistItem[];
  addOns?: { label: string; detail: string }[];
  whoFor: string[];
  whyItMatters: string[];
  miniFaq: { question: string; answer: string }[];
};

/* ---------------- Services ---------------- */

export const servicesContent: Record<ServiceSlug, ServiceContent> = {
  "house-cleaning": {
    slug: "house-cleaning",
    name: "House Cleaning",
    h1: "Recurring House Cleaning in Wetumpka, AL",
    intro:
      "Our recurring house cleaning keeps your home consistently fresh, tidy, and guest-ready week after week. We handle the everyday upkeep so you get your time back and a home that always feels cared for.",
    metaTitle: "House Cleaning in Wetumpka, AL | Recurring Home Cleaning",
    metaDescription:
      "Weekly, bi-weekly, and monthly house cleaning in Wetumpka, AL and surrounding areas. Dependable recurring home cleaning tailored to your home. Request a quote today.",
    heroImage: IMAGES.livingRoom1,
    heroAlt:
      "Living room with bay windows and plank floors vacuumed during a recurring house cleaning visit by Pretty in Pink Cleaning Service in Wetumpka, AL",
    whatsIncluded: [
      {
        label: "Dusting",
        detail: "Shelves, picture frames, baseboards, ceiling fans, and light fixtures.",
      },
      { label: "Surface wiping", detail: "Tables, counters, desks, and other reachable surfaces." },
      {
        label: "Kitchen detail",
        detail: "Countertops, stovetop, exterior of appliances, sink, and backsplash.",
      },
      {
        label: "Bathroom sanitizing",
        detail: "Sinks, counters, mirrors, toilets, tub, and shower.",
      },
      { label: "Floor care", detail: "Vacuuming carpets and rugs, then mopping hard floors." },
      { label: "Tidying", detail: "Straightening rooms, making beds, and emptying trash." },
      {
        label: "High-touch surfaces",
        detail: "Light switches, door knobs, and handles wiped down.",
      },
    ],
    whoFor: [
      "Busy families who want a clean home without spending the weekend on chores.",
      "Professionals who'd rather spend downtime relaxing than scrubbing.",
      "Homeowners who want a consistent, reliable clean on a set schedule.",
      "Anyone between deep cleans who wants their home to stay managed in between.",
    ],
    whyItMatters: [
      "A regular clean keeps dust, allergens, and grime from building up, so your home stays healthier between visits.",
      "Recurring visits let us learn your home and preferences, so the result gets more tailored over time.",
      "You set the cadence — weekly, bi-weekly, or monthly — based on how your household actually lives.",
      "Consistent upkeep means a deep clean is rarely an emergency; the baseline never gets out of hand.",
    ],
    miniFaq: [
      {
        question: "How often should I schedule recurring cleaning?",
        answer:
          "Most homes do great on a bi-weekly schedule. Busier households or homes with pets often prefer weekly, while quieter households do well monthly. We'll help you pick a cadence when you request a quote.",
      },
      {
        question: "Will the same team clean my home each time?",
        answer:
          "We aim for consistency. A regular cleaner who knows your home and preferences delivers better results, so we keep visits consistent whenever we can.",
      },
      {
        question: "Do I need to provide cleaning supplies?",
        answer:
          "We bring our own supplies and equipment by default. If you'd prefer we use specific products you provide, just let us know — we're happy to work with your preferences.",
      },
      {
        question: "Can I skip or reschedule a visit?",
        answer:
          "Yes. Life happens — vacations, guests, schedule changes. Just let us know in advance and we'll adjust your visit.",
      },
    ],
  },
  "deep-cleaning": {
    slug: "deep-cleaning",
    name: "Deep Cleaning",
    h1: "Deep Cleaning in Wetumpka, AL",
    intro:
      "A deep cleaning is a top-to-bottom detail clean that reaches the spots your regular routine misses. It's the reset your home needs after a busy stretch, before guests arrive, or when you simply want every corner to feel truly fresh.",
    metaTitle: "Deep Cleaning in Wetumpka, AL | Top-to-Bottom Detail Clean",
    metaDescription:
      "Top-to-bottom deep cleaning in Wetumpka, AL and surrounding areas. Detailed cleaning of baseboards, appliances, fixtures, and more. Request a quote today.",
    heroImage: IMAGES.bathroom2,
    heroAlt:
      "Bathtub with gray tile surround scrubbed shiny during a deep cleaning by Pretty in Pink Cleaning Service near Montgomery, AL",
    whatsIncluded: [
      { label: "Everything in a standard clean", detail: "All the basics, done more thoroughly." },
      { label: "Baseboards & trim", detail: "Wiped and detailed throughout the home." },
      { label: "Inside appliances", detail: "Interior of the oven and refrigerator as requested." },
      { label: "Cabinet fronts", detail: "Wiped down to remove grease and fingerprints." },
      { label: "Light fixtures & vents", detail: "Dusted and wiped where reachable." },
      { label: "Tile & grout", detail: "Scrubbed and brightened in bathrooms and kitchens." },
      { label: "Blinds & shutters", detail: "Dusted and wiped slat by slat." },
      { label: "Doorknobs & switch plates", detail: "Detailed and sanitized." },
    ],
    addOns: [
      {
        label: "Window cleaning",
        detail:
          "Interior glass, sills, and tracks. Available as an add-on for an additional charge. Just mention it when you request your quote.",
      },
    ],
    whoFor: [
      "Anyone whose home needs a full reset after months of routine upkeep falling behind.",
      "People preparing to host guests, holidays, or a big event at home.",
      "New clients who want to start a recurring plan with a clean baseline.",
      "Homeowners tackling spring cleaning or seasonal refreshes.",
    ],
    whyItMatters: [
      "Deep cleaning removes built-up grime, dust, and allergens that daily cleaning leaves behind.",
      "It's the ideal starting point before a recurring plan — we bring the home to a high baseline, then maintain it.",
      "Detailed cleaning of grout, vents, and high-touch spots keeps your home healthier and looking newer.",
      "A thorough clean also surfaces small maintenance issues — a scuff, a stain, a worn spot — before they become bigger.",
    ],
    miniFaq: [
      {
        question: "How long does a deep clean take?",
        answer:
          "It depends on the size and condition of your home. Most deep cleans take longer than a standard visit because we're working through every room in detail. We'll give you a time estimate with your quote.",
      },
      {
        question: "Do I need a deep clean before starting recurring service?",
        answer:
          "It's recommended. A deep clean brings your home to a fresh baseline, then recurring visits maintain that level. It's the most efficient way to keep things consistently clean.",
      },
      {
        question: "What's the difference between deep and standard cleaning?",
        answer:
          "A standard clean covers the everyday — surfaces, floors, kitchen, and bathrooms. A deep clean goes further: baseboards, inside appliances, grout, vents, and detail work that a regular visit doesn't reach.",
      },
      {
        question: "How often should I get a deep clean?",
        answer:
          "Many clients book a deep clean once or twice a year, with recurring standard cleans in between. Homes with pets, kids, or heavy use may benefit from one each season.",
      },
    ],
  },
  "move-in-move-out-cleaning": {
    slug: "move-in-move-out-cleaning",
    name: "Move-In / Move-Out Cleaning",
    h1: "Move-In & Move-Out Cleaning in Wetumpka, AL",
    intro:
      "Move-in and move-out cleaning gets an empty home spotless before you settle in or hand over the keys. We clean every surface, corner, and fixture so the space is fresh, sanitary, and ready for its next chapter.",
    metaTitle: "Move-In / Move-Out Cleaning in Wetumpka, AL",
    metaDescription:
      "Move-in and move-out cleaning in Wetumpka, AL and surrounding areas. Empty homes cleaned top to bottom — cabinets, appliances, and fixtures. Request a quote.",
    heroImage: IMAGES.moveOut1,
    heroAlt:
      "Empty, move-out-ready room cleaned top to bottom before new occupants arrive by Pretty in Pink Cleaning Service in Wetumpka, AL",
    whatsIncluded: [
      {
        label: "Empty-room cleaning",
        detail: "Walls, corners, and baseboards in cleared-out spaces.",
      },
      { label: "Inside cabinets & drawers", detail: "Wiped and vacuumed, inside and out." },
      { label: "Appliances", detail: "Interior and exterior of fridge, oven, and microwave." },
      { label: "Bathroom deep clean", detail: "Tub, shower, tile, grout, toilet, and fixtures." },
      { label: "Floors", detail: "Vacuumed and mopped, edges and corners included." },
      { label: "Light fixtures & vents", detail: "Dusted and wiped where reachable." },
      { label: "Trash & debris", detail: "Bagged and removed from the space." },
    ],
    addOns: [
      {
        label: "Window cleaning",
        detail:
          "Interior glass, sills, and tracks. Available as an add-on for an additional charge. Just mention it when you request your quote.",
      },
    ],
    whoFor: [
      "Sellers who want to leave the home in great shape for the next owner.",
      "Buyers moving into a home and wanting a truly fresh start.",
      "Renters getting a space ready for a final walk-through.",
      "Landlords and property managers preparing a unit for new tenants.",
    ],
    whyItMatters: [
      "An empty home is easier to clean thoroughly — no furniture in the way, so we can reach every surface.",
      "Move-out cleans help protect your deposit and leave a good impression for the next occupant.",
      "Moving is stressful enough; coming home to a freshly cleaned space makes the transition easier.",
      "Sanitizing an empty space before move-in means you're starting from a known-clean baseline.",
    ],
    miniFaq: [
      {
        question: "Does the home need to be empty?",
        answer:
          "Empty is best — it lets us reach every surface without moving furniture. If it's not fully empty yet, we can still help; just let us know what's there when you request a quote.",
      },
      {
        question: "Can you clean for a move-in and a move-out at the same property?",
        answer:
          "Yes. If you're buying and selling at once, we can clean both homes. Let us know both addresses and timelines with your quote request.",
      },
      {
        question: "Do you clean inside appliances and cabinets?",
        answer:
          "Yes — interior cleaning of the refrigerator, oven, and cabinets is part of a move clean, since that's when those areas are easiest to reach.",
      },
      {
        question: "How soon can you schedule a move clean?",
        answer:
          "We work around move dates whenever we can. Reach out as early as you can and we'll do our best to line the clean up with your timeline.",
      },
    ],
  },
  "commercial-office-cleaning": {
    slug: "commercial-office-cleaning",
    name: "Commercial & Office Cleaning",
    h1: "Commercial & Office Cleaning in Wetumpka, AL",
    intro:
      "Our commercial and office cleaning keeps your workplace clean, healthy, and presentable for staff and clients. We work around your hours — often after the workday ends — so your team walks into a fresh space each morning.",
    metaTitle: "Commercial & Office Cleaning in Wetumpka, AL",
    metaDescription:
      "Commercial and office cleaning in Wetumpka, AL and surrounding areas. Nightly and weekly cleaning for offices, clinics, salons, and small businesses. Request a quote.",
    heroImage: IMAGES.hallway1,
    heroAlt: "Tiled hallway swept and mopped by Pretty in Pink Cleaning Service in Wetumpka, AL",
    whatsIncluded: [
      { label: "Workstation wiping", detail: "Desks, monitors, keyboards, and phones sanitized." },
      {
        label: "Restroom sanitizing",
        detail: "Toilets, sinks, mirrors, and restocking as arranged.",
      },
      { label: "Breakroom cleaning", detail: "Counters, sink, microwave interior, and tables." },
      { label: "Floor care", detail: "Vacuuming carpeted areas and mopping hard floors." },
      { label: "Trash removal", detail: "Bins emptied and liners replaced." },
      { label: "High-touch surfaces", detail: "Doors, handles, and light switches disinfected." },
      { label: "Glass & entryways", detail: "Interior glass and entry doors wiped down." },
      { label: "Common areas", detail: "Lobbies, hallways, and meeting rooms tidied." },
    ],
    whoFor: [
      "Small offices that need a reliable nightly or weekly clean.",
      "Clinics and wellness practices that need sanitary, presentable spaces.",
      "Salons, studios, and retail spaces with regular foot traffic.",
      "Property managers overseeing shared workspaces and suites.",
    ],
    whyItMatters: [
      "A clean workplace reduces germs and sick days, keeping your team healthier.",
      "First impressions matter — a tidy office signals professionalism to clients and visitors.",
      "Regular cleaning protects your floors, surfaces, and fixtures, extending their life.",
      "Outsourcing cleaning lets your team focus on their work instead of tidying up.",
    ],
    miniFaq: [
      {
        question: "Can you clean after hours?",
        answer:
          "Yes — most commercial work happens after the business day ends so we don't interrupt your team. We'll set a schedule that fits your hours.",
      },
      {
        question: "How often should an office be cleaned?",
        answer:
          "It depends on foot traffic and use. Many offices do best with nightly cleaning; smaller, quieter spaces may need two or three visits a week. We'll recommend a schedule with your quote.",
      },
      {
        question: "Can you handle restrooms and breakrooms?",
        answer:
          "Yes. Restrooms and breakrooms are part of most commercial plans — we sanitize restrooms and wipe down breakroom surfaces, sinks, and microwaves.",
      },
      {
        question: "Are you insured to work in commercial spaces?",
        answer:
          "Yes. We're set up to clean commercial spaces and can share the details you need for your records. Just ask when you request a quote.",
      },
    ],
  },
  "post-construction-cleaning": {
    slug: "post-construction-cleaning",
    name: "Post-Construction Cleaning",
    h1: "Post-Construction Cleaning in Wetumpka, AL",
    intro:
      "Post-construction cleaning removes the dust, debris, and residue left after a remodel or build so your space is truly move-in ready. We go beyond a normal clean to get every surface back to clean, from vents to baseboards.",
    metaTitle: "Post-Construction Cleaning in Wetumpka, AL",
    metaDescription:
      "Post-construction cleaning in Wetumpka, AL and surrounding areas. Dust removal, surface detailing, and final clean for remodeled or newly built spaces. Request a quote.",
    heroImage: IMAGES.floors1,
    heroAlt:
      "Gleaming, dust-free wood floors in a newly cleaned space after construction, cleaned by Pretty in Pink Cleaning Service in Wetumpka, AL",
    whatsIncluded: [
      {
        label: "Dust removal",
        detail: "Walls, ceilings, vents, and light fixtures dusted top-down.",
      },
      { label: "Floor detailing", detail: "Vacuuming, sweeping, and mopping, edges included." },
      {
        label: "Cabinet & drawer cleaning",
        detail: "Inside and out, cleared of sawdust and dust.",
      },
      { label: "Fixture detailing", detail: "Light switches, outlets, and hardware wiped clean." },
      { label: "Vent & register cleaning", detail: "Supply and return vents vacuumed and wiped." },
      { label: "Surface sanitizing", detail: "Counters, sinks, and appliances wiped down." },
      { label: "Debris sweep", detail: "Loose construction debris and leftover bits bagged." },
    ],
    addOns: [
      {
        label: "Window cleaning",
        detail:
          "Interior glass, sills, and tracks. Available as an add-on for an additional charge. Just mention it when you request your quote.",
      },
    ],
    whoFor: [
      "Homeowners finishing a remodel and ready to enjoy the results.",
      "Contractors who need a final clean before handing over a project.",
      "Property owners preparing a newly built or renovated space to live in or list.",
      "Businesses reopening after a renovation or tenant build-out.",
    ],
    whyItMatters: [
      "Construction dust is fine and pervasive — it settles in vents, tracks, and on every surface, and a normal clean won't remove it all.",
      "Removing dust from vents and registers keeps it from circulating once you move back in.",
      "A proper post-construction clean protects new finishes — floors, cabinets, and counters — so they look their best from day one.",
      "It's the difference between 'looks done' and 'feels truly clean and ready to use.'",
    ],
    miniFaq: [
      {
        question: "When should post-construction cleaning be scheduled?",
        answer:
          "After all construction is finished and the space is cleared. We can do a rough clean mid-project and a final clean at the end — let us know your timeline when you request a quote.",
      },
      {
        question: "Is a post-construction clean different from a deep clean?",
        answer:
          "Yes. A deep clean details lived-in spaces; a post-construction clean targets construction dust, debris, and residue specifically — vents, tracks, and fine dust that a deep clean doesn't address.",
      },
      {
        question: "Do you remove leftover construction debris?",
        answer:
          "We sweep and bag loose dust and small bits. Hauling large debris or materials is a separate service — let us know what's on site and we'll clarify what's included.",
      },
      {
        question: "Can you clean a single renovated room?",
        answer:
          "Yes. If you remodeled one bathroom or kitchen, we can clean just that area — or the whole home if dust spread throughout.",
      },
    ],
  },
};

/* ---------------- FAQ page (15+) ---------------- */

export const faqItems: { category: string; question: string; answer: string }[] = [
  {
    category: "Services",
    question: "What cleaning services do you offer?",
    answer:
      "We offer residential and commercial cleaning — recurring house cleaning, deep cleaning, move-in/move-out cleaning, commercial and office cleaning, and post-construction cleaning. Each is tailored to the space and how it's used.",
  },
  {
    category: "Services",
    question: "What's included in a standard house cleaning?",
    answer:
      "A standard visit includes dusting, wiping surfaces, sanitizing the kitchen and bathrooms, vacuuming, and mopping floors. Deep cleaning and move cleans add detail work like baseboards, inside appliances, and grout.",
  },
  {
    category: "Scheduling",
    question: "How often can I schedule cleaning?",
    answer:
      "We offer weekly, bi-weekly, and monthly recurring visits, plus one-time cleans like deep cleans and move cleans. You pick the cadence that fits your home; we'll help you decide with your quote.",
  },
  {
    category: "Scheduling",
    question: "Do I need to be home during the cleaning?",
    answer:
      "No. Most clients aren't home during their clean. As long as we can access the space, we can clean while you're out and lock up when we're done. Just let us know your access preference when you book.",
  },
  {
    category: "Scheduling",
    question: "Can I skip or reschedule a recurring visit?",
    answer:
      "Yes. Vacations, guests, and schedule changes happen — just let us know ahead of time and we'll adjust your visit. We ask for reasonable notice so we can keep the calendar running smoothly.",
  },
  {
    category: "What to expect",
    question: "What should I do before the cleaning team arrives?",
    answer:
      "A quick tidy helps — picking up clothes, toys, and clutter lets us focus on cleaning rather than organizing. You don't need to pre-clean. Let us know of any areas you'd like us to focus on or skip.",
  },
  {
    category: "What to expect",
    question: "How long does a cleaning take?",
    answer:
      "It depends on the size and condition of your home and the type of clean. A standard recurring visit is quicker than a deep clean or move clean. We'll give you a time estimate with your quote.",
  },
  {
    category: "Supplies",
    question: "Do you bring your own cleaning supplies and equipment?",
    answer:
      "Yes. We bring our own supplies and equipment by default. If you'd prefer we use specific products you provide — for allergies or preferences — just let us know and we'll work with them.",
  },
  {
    category: "Supplies",
    question: "What products do you use?",
    answer:
      "We use a mix of general cleaning products chosen to be effective and safe for the surfaces we're cleaning. If you have sensitivities or want us to use a particular product, tell us and we'll do our best to accommodate.",
  },
  {
    category: "Supplies",
    question: "Are you okay working around pets?",
    answer:
      "Yes, we're pet-friendly. Please let us know about pets when you book so we can plan around them — and let us know if any need to be secured during the visit for their comfort and our safety.",
  },
  {
    category: "Residential vs. commercial",
    question: "What's the difference between residential and commercial cleaning?",
    answer:
      "Residential cleaning focuses on homes — recurring upkeep, deep cleans, and move cleans. Commercial cleaning targets workplaces — offices, clinics, salons — usually after hours, with a focus on sanitizing high-touch and shared spaces.",
  },
  {
    category: "Residential vs. commercial",
    question: "Can you clean both my home and my business?",
    answer:
      "Yes. We serve both residential and commercial clients across our service area. If you have both, we can set up separate plans for each.",
  },
  {
    category: "Move-out cleans",
    question: "How does a move-out cleaning work?",
    answer:
      "We clean an empty home top to bottom — inside cabinets and appliances, floors, bathrooms, and details — so it's ready for the next occupant or a final walk-through. Empty homes let us reach every surface.",
  },
  {
    category: "Move-out cleans",
    question: "Does the home need to be empty for a move clean?",
    answer:
      "Empty is best, since we can reach every surface without moving furniture. If it's not fully empty yet, tell us what's there when you request a quote and we'll work with it.",
  },
  {
    category: "Service area",
    question: "What areas do you serve?",
    answer:
      "We're based in Wetumpka, AL and serve the surrounding River Region — Wetumpka, Millbrook, Prattville, Montgomery, Tallassee, and Eclectic. If you're nearby, reach out and we'll confirm we can help.",
  },
  {
    category: "Quotes & contact",
    question: "How do I get a quote?",
    answer:
      "Fill out the quote form on our Contact page or call us at (334) 202-3594. Tell us the size and type of space and what you'd like cleaned, and we'll put together a quote — no obligation.",
  },
];

/* ---------------- City-specific content (unique beyond the blurb) ---------------- */

export const cityContent: Record<
  CitySlug,
  { intro: string; localNote: string; cta: string; heroImage: string; heroAlt: string }
> = {
  "wetumpka-al": {
    intro:
      "Pretty in Pink Cleaning Service is based right here in Wetumpka, and our recurring house cleaning, deep cleaning, move-in/move-out, and commercial cleaning services are available throughout the city. From homes near downtown to businesses along Highway 231, we bring dependable, detail-driven cleaning to our neighbors across Wetumpka.",
    localNote:
      "Wetumpka sits along the Tallapoosa River at the edge of the crater, a small-town community with a big sense of place. We're proud to clean the homes and businesses that make Wetumpka feel like home.",
    cta: "Wetumpka neighbors — request a quote and let us handle the cleaning.",
    heroImage: IMAGES.hero,
    heroAlt:
      "Kitchen with granite counters and mosaic backsplash cleaned by Pretty in Pink Cleaning Service in Wetumpka, AL",
  },
  "millbrook-al": {
    intro:
      "We bring our full range of cleaning services to Millbrook — recurring house cleaning, deep cleaning, move-in/move-out cleaning, and commercial cleaning for the growing neighborhoods and local businesses here. If you're in Millbrook, we're close by and ready to help.",
    localNote:
      "Millbrook has grown fast in recent years, with quiet residential streets and busy local shops just south of Wetumpka. We're glad to keep Millbrook homes and workspaces clean door to door.",
    cta: "Millbrook homes and offices — request a quote and we'll take it from there.",
    heroImage: IMAGES.kitchenClean3,
    heroAlt:
      "Dark-cabinet kitchen with granite counters cleaned by Pretty in Pink Cleaning Service near Millbrook, AL",
  },
  "prattville-al": {
    intro:
      "Prattville homeowners and businesses can rely on us for recurring house cleaning, deep cleaning, move-in/move-out cleaning, and commercial cleaning. We know the Fountain City well and bring consistent, careful cleaning to homes and offices across Prattville.",
    localNote:
      "Prattville blends historic charm near Autauga Creek with busy modern neighborhoods and retail. We keep up with the pace, cleaning homes and businesses through every Prattville season.",
    cta: "Prattville friends — request a quote for cleaning that fits your schedule.",
    heroImage: IMAGES.livingRoom2,
    heroAlt:
      "Living room with fireplace and French doors dusted by Pretty in Pink Cleaning Service near Prattville, AL",
  },
  "montgomery-al": {
    intro:
      "Our cleaning services extend across greater Montgomery, where we clean residential homes and commercial spaces alike — recurring house cleaning, deep cleaning, move-in/move-out, and office cleaning. From downtown offices to suburban homes, we serve the capital city and its surroundings.",
    localNote:
      "Montgomery is Alabama's capital and the hub of the River Region, full of downtown offices, clinics, and family neighborhoods. We bring dependable cleaning to businesses and residences throughout the greater Montgomery area.",
    cta: "Montgomery homes and offices — request a quote for cleaning on your schedule.",
    heroImage: IMAGES.bathroom1,
    heroAlt:
      "Double vanity with dark cabinets in a bathroom cleaned by Pretty in Pink Cleaning Service near Montgomery, AL",
  },
  "tallassee-al": {
    intro:
      "We're glad to serve Tallassee with recurring house cleaning, deep cleaning, move-in/move-out cleaning, and commercial cleaning. Whether you're keeping up a family home or a local business, we bring reliable cleaning to Tallassee and nearby Elmore County.",
    localNote:
      "Tallassee sits along the Tallapoosa River with a tight-knit, family-friendly feel. We take care of the cleaning so Tallassee residents can spend more time enjoying the community.",
    cta: "Tallassee residents — request a quote and leave the cleaning to us.",
    heroImage: IMAGES.bedroom1,
    heroAlt:
      "Empty bedroom with wood floors and blinds dusted by Pretty in Pink Cleaning Service near Tallassee, AL",
  },
  "eclectic-al": {
    intro:
      "Eclectic is a small, relaxed community near Lake Martin, and we're proud to serve it with our full cleaning services — recurring house cleaning, deep cleaning, move-in/move-out, and commercial cleaning for weekend cabins and full-time homes alike.",
    localNote:
      "Life in Eclectic moves at a lakeside pace. From weekend cabins to year-round homes and local businesses, we keep Eclectic spaces clean so you can enjoy the lake life.",
    cta: "Eclectic homeowners — request a quote and let us keep it clean.",
    heroImage: IMAGES.floors2,
    heroAlt:
      "Fireplace room with freshly mopped plank floors cleaned by Pretty in Pink Cleaning Service near Eclectic, AL",
  },
};
